import {FIELD_MAP, buildTemplateHeaders} from './wofQuizConstants';

export const INVALID_FORMAT_ERROR =
  'The uploaded file is invalid. Please check the data format and try again.';

export const DUPLICATE_QUESTIONS_ERROR =
  'The uploaded quiz contains duplicate questions. Please remove duplicates and try again.';

const STOP_WORDS = new Set([
  'a',
  'an',
  'the',
  'is',
  'are',
  'was',
  'were',
  'be',
  'been',
  'being',
  'have',
  'has',
  'had',
  'do',
  'does',
  'did',
  'will',
  'would',
  'could',
  'should',
  'may',
  'might',
  'must',
  'shall',
  'can',
  'need',
  'dare',
  'ought',
  'used',
  'to',
  'of',
  'in',
  'for',
  'on',
  'with',
  'at',
  'by',
  'from',
  'as',
  'into',
  'through',
  'during',
  'before',
  'after',
  'above',
  'below',
  'between',
  'under',
  'and',
  'but',
  'or',
  'yet',
  'so',
  'if',
  'because',
  'although',
  'though',
  'while',
  'where',
  'when',
  'that',
  'which',
  'who',
  'whom',
  'whose',
  'what',
  'this',
  'these',
  'those',
  'i',
  'you',
  'he',
  'she',
  'it',
  'we',
  'they',
  'me',
  'him',
  'her',
  'us',
  'them',
  'my',
  'your',
  'his',
  'its',
  'our',
  'their'
]);

export const normalizeQuestion = (str) =>
  str
    .normalize('NFKD')
    .toLowerCase()
    .replace(/[\p{P}\p{S}]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();

export const tokenNormalizeQuestion = (str) => {
  const tokens = normalizeQuestion(str)
    .split(' ')
    .filter((token) => !STOP_WORDS.has(token));
  tokens.sort();
  return tokens.join(' ');
};

export const levenshteinDistance = (a, b) => {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;

  const prev = new Array(n + 1);
  const curr = new Array(n + 1);

  for (let j = 0; j <= n; j++) {
    prev[j] = j;
  }

  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    const ai = a[i - 1];
    for (let j = 1; j <= n; j++) {
      const cost = ai === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= n; j++) {
      prev[j] = curr[j];
    }
  }

  return prev[n];
};

export const similarityScore = (a, b) => {
  if (a === b) return 1;
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  const distance = levenshteinDistance(a, b);
  return 1 - distance / maxLen;
};

export const validateCsvHeaders = (headers, languages) => {
  const expected = buildTemplateHeaders(languages);
  const missing = expected.filter((h) => !headers.includes(h));
  const unexpected = headers.filter((h) => h && !expected.includes(h));

  if (unexpected.some((h) => /^_\d+$/.test(h))) {
    return INVALID_FORMAT_ERROR;
  }

  const errors = [];
  if (missing.length > 0) {
    errors.push(`Missing required columns: ${missing.join(', ')}`);
  }
  if (unexpected.length > 0) {
    errors.push(`Unexpected columns: ${unexpected.join(', ')}`);
  }
  return errors.join('. ');
};

export const getMissingColumns = (row, lang) => {
  const suffix = lang.code;
  return FIELD_MAP.filter((f) => !row[`${f.header} ${suffix}`]?.trim()).map(
    (f) => `${f.header} ${suffix}`
  );
};

export const parseCsvToQuestions = (rows, languages) =>
  rows.map((row, idx) => {
    const translations = {};
    languages.forEach((lang) => {
      const suffix = lang.code;
      const t = {};
      FIELD_MAP.forEach((f) => {
        t[f.prop] = row[`${f.header} ${suffix}`]?.trim() || '';
      });
      translations[suffix] = t;
    });
    return {id: String(idx + 1), translations};
  });

export const findDuplicateQuestions = (rows, languages) => {
  const exact = [];
  const fuzzy = [];

  languages.forEach((lang) => {
    const suffix = lang.code;
    const header = `Question ${suffix}`;
    const seen = new Map();
    const rawQuestions = [];

    rows.forEach((row) => {
      const raw = row[header];
      if (!raw) return;
      const trimmed = raw.trim();
      const normalized = normalizeQuestion(trimmed);
      const tokenNormalized = tokenNormalizeQuestion(trimmed);

      if (seen.has(normalized)) {
        const existing = seen.get(normalized);
        if (!exact.includes(existing)) {
          exact.push(existing);
        }
        if (!exact.includes(trimmed)) {
          exact.push(trimmed);
        }
      } else {
        seen.set(normalized, trimmed);
      }

      rawQuestions.push({raw: trimmed, normalized, tokenNormalized});
    });

    for (let i = 0; i < rawQuestions.length; i++) {
      const a = rawQuestions[i];
      for (let j = i + 1; j < rawQuestions.length; j++) {
        const b = rawQuestions[j];

        const alreadyExact = exact.includes(a.raw) && exact.includes(b.raw);
        if (alreadyExact) continue;

        const tokenMatch =
          a.tokenNormalized.length > 0 &&
          a.tokenNormalized === b.tokenNormalized;

        const fuzzyMatch = similarityScore(a.normalized, b.normalized) >= 0.9;

        if (tokenMatch || fuzzyMatch) {
          if (!fuzzy.includes(a.raw)) {
            fuzzy.push(a.raw);
          }
          if (!fuzzy.includes(b.raw)) {
            fuzzy.push(b.raw);
          }
        }
      }
    }
  });

  return {exact, fuzzy};
};
